// hooks/useBowler.js
import { useEffect, useState } from "react";
import { supabase } from "../supabaseClient";
import { useOrganization } from "../context/OrganizationContext";
import { useAuth } from "../context/AuthContext";
import { canAccessBowlerContactInfo } from "../utils/authorization";

const useBowler = (organizationId = null) => {
  const [existingBowlers, setExistingBowlers] = useState([]);
  const [error, setError] = useState(null);
  const [canAccessContactInfo, setCanAccessContactInfo] = useState(false);
  const { currentOrganization } = useOrganization();
  const { user } = useAuth();

  // Use provided organizationId or fall back to current organization
  const targetOrganizationId = organizationId || currentOrganization?.id;

  useEffect(() => {
    const fetchExistingBowlers = async () => {
      if (!targetOrganizationId) {
        setExistingBowlers([]);
        return;
      }

      try {
        // Check if user can access contact information
        if (user) {
          const { canAccessContactInfo: hasPermission } =
            await canAccessBowlerContactInfo(user.id, targetOrganizationId);
          setCanAccessContactInfo(hasPermission);
        }

        // Build the select query based on permissions
        let selectQuery = "id, name, usbcID, created_at, organizationID";

        if (canAccessContactInfo) {
          // If user has permission, also fetch contact info from bowlersContact table
          const { data, error } = await supabase
            .from("bowlers")
            .select(
              `
              id, 
              name, 
              usbcID, 
              created_at, 
              organizationID,
              bowlersContact (
                id,
                type,
                value,
                isPrimary
              )
            `
            )
            .eq("organizationID", targetOrganizationId)
            .is("deletedAt", null);

          if (error) {
            setError(error);
          } else {
            // Transform the data to include contact info in the expected format
            const transformedData = data.map((bowler) => {
              const contactInfo = bowler.bowlersContact || [];
              const email =
                contactInfo.find((c) => c.type === "email" && c.isPrimary)
                  ?.value ||
                contactInfo.find((c) => c.type === "email")?.value ||
                null;
              const phone =
                contactInfo.find((c) => c.type === "phone" && c.isPrimary)
                  ?.value ||
                contactInfo.find((c) => c.type === "phone")?.value ||
                null;

              return {
                ...bowler,
                email,
                phone,
              };
            });
            setExistingBowlers(transformedData);
          }
        } else {
          // If user doesn't have permission, only fetch basic info
          const { data, error } = await supabase
            .from("bowlers")
            .select(selectQuery)
            .eq("organizationID", targetOrganizationId)
            .is("deletedAt", null);

          if (error) {
            setError(error);
          } else {
            setExistingBowlers(data);
          }
        }
      } catch (err) {
        setError(err);
      }
    };

    fetchExistingBowlers();
  }, [targetOrganizationId, user, canAccessContactInfo]);

  const addBowlerToDatabase = async (bowler) => {
    if (!targetOrganizationId) {
      setError("No organization selected");
      return null;
    }

    try {
      // Insert bowler basic info
      const { data: bowlerData, error: bowlerError } = await supabase
        .from("bowlers")
        .insert([
          {
            name: bowler.name,
            usbcID: bowler.usbcID,
            organizationID: targetOrganizationId,
            createdBy: user?.id,
            updatedBy: user?.id,
          },
        ])
        .select()
        .single();

      if (bowlerError) throw bowlerError;

      // If contact info is provided and user has permission, insert it
      if (canAccessContactInfo && (bowler.email || bowler.phone)) {
        const contactEntries = [];

        if (bowler.email) {
          contactEntries.push({
            bowlerID: bowlerData.id,
            type: "email",
            value: bowler.email,
            isPrimary: true,
            createdBy: user?.id,
            created: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          });
        }

        if (bowler.phone) {
          contactEntries.push({
            bowlerID: bowlerData.id,
            type: "phone",
            value: bowler.phone,
            isPrimary: true,
            createdBy: user?.id,
            created: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          });
        }

        if (contactEntries.length > 0) {
          const { error: contactError } = await supabase
            .from("bowlersContact")
            .insert(contactEntries);

          if (contactError) {
            console.error("Error inserting contact info:", contactError);
            // Don't fail the entire operation if contact info fails
          }
        }
      }

      return bowlerData.id;
    } catch (error) {
      setError(error);
      return null;
    }
  };

  return {
    existingBowlers,
    addBowlerToDatabase,
    error,
    canAccessContactInfo,
    organizationId: targetOrganizationId,
  };
};

export default useBowler;
